import { lineQtyText } from '../lib/bill-view.ts'
import {
  assignLineProducts,
  buildCreditNote,
  buildPayment,
  cancelInvoice,
  formatQty,
  stockReturn,
  unassignLineProduct,
  type PaymentMethod,
  type SyncOp,
  mulDivRound,
} from '@ims/core'
import type {
  InvoiceLineRow,
  InvoicePayment,
  InvoiceRow,
  LineProductRow,
  ProductRow,
} from '@ims/data'
import {
  arrivalClass,
  Badge,
  Button,
  buttonClass,
  Card,
  Checkbox,
  ConfirmDialog,
  cx,
  EmptyState,
  Field,
  FilterPill,
  FilterPills,
  IconButton,
  IconTile,
  Kbd,
  Input,
  ListFrame,
  ListToolbar,
  Modal,
  PageHeader,
  Pagination,
  RowActions,
  RowNote,
  RowTitle,
  SearchInput,
  Select,
  SkeletonDetail,
  SkeletonTable,
  StateSwap,
  Table,
  TD,
  Textarea,
  TH,
  TR,
  useArrivals,
  useToast,
} from '@ims/ui'
import {
  ArrowLeft,
  Ban,
  FileText,
  Check,
  Copy,
  Link2,
  PackagePlus,
  Share2,
  Printer,
  Receipt as ReceiptIcon,
  RotateCcw,
  Wallet,
  X,
  Banknote,
} from '@ims/ui/icons'
import { useRef, useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { LocationSelect, MoneyInput, ProductPicker, QtyInput, useLookups } from '../components.tsx'
import { errorMessage, useBatchLabels, useLive, useOnline, useSettings } from '../hooks.ts'
import { createApi } from '../lib/api.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { loadBatchStock, planBatchLines } from '../lib/batches.ts'
import { stockShortfalls, type StockShortfall } from '../lib/cart.ts'
import { dateLabel, dateShort, money, todayIso } from '../lib/format.ts'
import { loadRecipes } from '../lib/recipes.ts'
import { numberingFor } from '../lib/numbering.ts'
import { PAYMENT_LABELS, paymentStatus } from '../lib/payment-status.ts'
import { daysBetween, dueWords, type Standing } from '../lib/purchases.ts'
import { BillPrintMenu, openPrintMenu } from './print-menu.tsx'
import { PrintDialog } from './print-dialog.tsx'
import { useServerName } from './server-dialog.tsx'
import { invoiceOrderLine } from '../lib/orders.ts'
import { TYPE_LOOK } from './service.tsx'
import { lineVatLabel, registrationOf, showsVat } from '../lib/registration.ts'
import { batchText, serialText, type BatchLabels } from '../print.tsx'
import { cancelSerialOps, lineSerials, loadSerials, returnSerialOps } from '../lib/serials.ts'
import type { SerialRow } from '../lib/rows.ts'
import { QrCode } from '../qr.tsx'
import { useSession } from '../session.tsx'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { finePointer } from '../lib/pointer.ts'
import { SectionTabs } from '../layout/sections.tsx'

const PAGE = 25
const INVOICE_TABLES = [
  'sales_invoices',
  'sales_invoice_lines',
  'invoice_line_products',
  'payments',
  'payment_allocations',
  'credit_notes',
] as const

/** A cancelled bill's row: everything but its badge is struck through. */
export const CANCELLED_ROW = 'text-muted line-through'

const PAYMENT_TONES = {
  paid: 'success',
  partial: 'warning',
  unpaid: 'warning',
  overdue: 'danger',
} as const

/**
 * A bill's state in one badge. A finalized bill shows what it is owed (Paid, Part paid,
 * Unpaid, Overdue) when `balancePaisa` is known, and "Finalized" while it is not.
 */
export function StatusBadge({
  invoice,
  balancePaisa,
}: {
  invoice: Pick<InvoiceRow, 'status' | 'number' | 'total_paisa' | 'due_date'>
  /** What is still owed on it; leave out (or null) when that is not known. */
  balancePaisa?: number | null
}) {
  if (invoice.status === 'cancelled') return <Badge tone="danger">Cancelled</Badge>
  if (invoice.status === 'draft') return <Badge>Draft</Badge>
  if (!invoice.number) return <Badge tone="warning">Syncing</Badge>
  if (balancePaisa == null) return <Badge tone="success">Finalized</Badge>
  const status = paymentStatus(invoice.total_paisa, balancePaisa, invoice.due_date, todayIso())
  return <Badge tone={PAYMENT_TONES[status]}>{PAYMENT_LABELS[status]}</Badge>
}

/**
 * What every finalized bill still owes, by invoice id (a bill that is not in the map owes
 * nothing). Undefined while loading; null where balances are not available (online-only mode).
 */
export function useOpenBalances(): ReadonlyMap<string, number> | null | undefined {
  return useLive(
    async (repo) =>
      repo.reports
        ? new Map((await repo.reports.openInvoices()).map((o) => [o.invoiceId, o.balance]))
        : null,
    [],
    ['sales_invoices', 'credit_notes', 'payments', 'payment_allocations'],
  ).data
}

/** The balance to give `StatusBadge` for a row of a list. */
export function balanceOf(
  open: ReadonlyMap<string, number> | null | undefined,
  invoiceId: string,
): number | null {
  return open ? (open.get(invoiceId) ?? 0) : null
}

type InvoicePill = '' | 'finalized' | 'draft' | 'cancelled' | InvoicePayment
/** The pills that count what needs dealing with. */
const COUNTED: InvoicePill[] = ['due', 'unpaid', 'partial', 'overdue', 'draft']
const ROW_BUTTON = 'h-[34px] text-[13px]'
const ROW_ICON =
  'size-[34px] border border-border bg-surface text-ink max-sm:size-10! max-sm:flex-none!'
/** The row the arrow keys are on (as on Quotations and Orders). */
const ROW_ACTIVE = 'sm:bg-sell-wash sm:shadow-[inset_3px_0_0_var(--color-primary)]'

/** Where a bill stands in two short lines: what is owed and since when, or that it is paid. */
function invoiceStanding(
  inv: InvoiceRow,
  balance: number | null,
  today: string,
  show: (iso: string) => string,
  amount: (paisa: number) => string,
): Standing {
  if (inv.status === 'cancelled') return { main: 'Cancelled', sub: inv.cancel_reason ?? undefined }
  if (inv.status === 'draft') return { main: 'Not finished' }
  if (!inv.number) return { main: 'Waiting for its number' }
  if (balance === null) return { main: 'Finalized' }
  if (balance <= 0) return { main: 'Paid', tone: 'success' }
  const late = !!inv.due_date && inv.due_date.slice(0, 10) < today
  const age = daysBetween(inv.invoice_date.slice(0, 10), today)
  return {
    main: `${amount(balance)} due`,
    sub: inv.due_date
      ? dueWords(inv.due_date.slice(0, 10), today, show, 'By').text
      : age <= 0
        ? 'From today'
        : `${age} day${age === 1 ? '' : 's'} old`,
    tone: late ? 'danger' : 'warning',
  }
}

export function InvoicesScreen() {
  const settings = useSettings()
  const openItem = useOpen()
  const toast = useToast()
  const { current } = useWorkspace()
  const [search, setSearch] = useState('')
  // One filter for both kinds of state: a payment state (finalized bills), or draft / cancelled.
  const [status, setStatusValue] = useState<InvoicePill>('')
  const byDocument = status === 'finalized' || status === 'draft' || status === 'cancelled'
  const [offset, setOffset] = useState(0)
  const canManage = useCan('manager')
  const canPay = useCan('waiter')
  const [params] = useSearchParams()
  // Bills with a custom item no product has been assigned to yet (managers).
  const [toAssign, setToAssign] = useState(params.get('assign') === '1')
  const unassignedCustom = toAssign && canManage
  const [paying, setPaying] = useState<{ invoice: InvoiceRow; balance: number } | null>(null)
  const [busy, setBusy] = useState(false)
  const setStatus = (next: InvoicePill) => {
    setStatusValue(next)
    setOffset(0)
  }
  const page = useLive(
    (repo) =>
      repo.invoices.list({
        search,
        status: byDocument ? status : undefined,
        payment: status && !byDocument ? status : undefined,
        unassignedCustom,
        limit: PAGE,
        offset,
      }),
    [search, status, unassignedCustom, offset],
    [...INVOICE_TABLES],
  )
  // A restaurant's bills say which table (or takeaway number) and who served it.
  const restaurant = Boolean(settings?.restaurant)
  const pageIds = (page.data?.rows ?? []).map((r) => r.id).join(',')
  const orderInfo = useLive(
    async (r) =>
      restaurant && pageIds
        ? new Map((await r.invoices.orderInfo(pageIds.split(','))).map((i) => [i.invoiceId, i]))
        : null,
    [restaurant, pageIds],
    ['orders', 'dining_tables'],
  ).data
  const repo = useRepo()
  const serverName = useServerName()
  const open = useOpenBalances()
  const today = todayIso()
  // Payment states need the device's copy of payments (not in online-only mode).
  const pills: { value: InvoicePill; label: string }[] = [
    { value: '', label: 'All' },
    ...(repo.reports
      ? ([
          { value: 'due', label: 'Money owed' },
          { value: 'unpaid', label: 'Unpaid' },
          { value: 'partial', label: 'Part paid' },
          { value: 'overdue', label: 'Overdue' },
          { value: 'paid', label: 'Paid' },
        ] as const)
      : ([{ value: 'finalized', label: 'Finalized' }] as const)),
    { value: 'draft', label: 'Draft' },
    { value: 'cancelled', label: 'Cancelled' },
  ]
  // How many bills each pill that needs attention holds.
  // A bill made on another device slides into the list it reaches.
  const arrived = useArrivals(
    page.data?.rows.map((r) => r.id) ?? [],
    page.data !== undefined && !page.loading,
    JSON.stringify([search, status, unassignedCustom, offset]),
  )
  const counts = useLive(
    async (r) => {
      if (!r.reports) return null
      const of = (value: InvoicePill) =>
        r.invoices
          .list(
            value === 'draft'
              ? { status: 'draft', limit: 1 }
              : { payment: value as InvoicePayment, limit: 1 },
          )
          .then((p) => [value, p.total] as const)
      return Object.fromEntries(await Promise.all(COUNTED.map(of))) as Record<string, number>
    },
    [],
    [...INVOICE_TABLES],
  ).data
  const owed = open ? [...open.values()].reduce((sum, b) => sum + Math.max(0, b), 0) : 0
  const owing = open ? [...open.values()].filter((b) => b > 0).length : 0

  // The keys: the arrows move a selected row, Enter opens it, P prints it, T takes payment.
  const [active, setActive] = useState<string | null>(null)
  const rows = page.data?.rows ?? []
  const activeRow = rows.find((r) => r.id === active)
  const printable = (inv: InvoiceRow) => inv.status === 'finalized' && !!inv.number
  const owesOn = (inv: InvoiceRow) =>
    inv.status === 'finalized' && (balanceOf(open, inv.id) ?? 0) > 0 && canPay
  const listRef = useRef<HTMLDivElement>(null)
  useListKeys({
    ids: rows.map((r) => r.id),
    active,
    setActive,
    page: { offset, limit: PAGE, total: page.data?.total ?? 0, setOffset },
    list: listRef,
    open: () => activeRow && openItem(`/invoices/${activeRow.id}`),
    keys: {
      p: () => activeRow && printable(activeRow) && openPrintMenu(activeRow.id),
      t: () =>
        activeRow &&
        owesOn(activeRow) &&
        setPaying({ invoice: activeRow, balance: balanceOf(open, activeRow.id)! }),
    },
  })

  const pay = async (amount: number, method: PaymentMethod, reference: string) => {
    if (!paying || !current) return
    setBusy(true)
    try {
      await repo.commit(
        buildPayment(
          { tenantId: current.tenant_id, now: new Date().toISOString() },
          {
            customerId: paying.invoice.customer_id,
            amountPaisa: amount,
            method,
            paymentDate: todayIso(),
            reference: reference || null,
            allocations: [{ invoiceId: paying.invoice.id, amountPaisa: amount }],
          },
        ).ops,
      )
      toast.success('Payment recorded')
      setPaying(null)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="Sales"
        subtitle={
          owed > 0 ? (
            <>
              <b className="font-semibold text-ink">{money(owed, settings)} owed to you</b> on{' '}
              {owing} bill{owing === 1 ? '' : 's'}
              {(counts?.overdue ?? 0) > 0 && (
                <span className="font-semibold text-danger"> · {counts!.overdue} overdue</span>
              )}
              {' · '}Every sale, newest first.
            </>
          ) : (
            'Every sale, including ones made offline.'
          )
        }
        actions={
          <Link to="/bill" className={buttonClass()}>
            <ReceiptIcon className="size-4" aria-hidden />
            New bill
          </Link>
        }
      />
      <SectionTabs section="sales" />
      <ListToolbar>
        <FilterPills label="Status">
          {pills.map((p) => (
            <FilterPill
              key={p.value || 'all'}
              selected={status === p.value}
              count={counts?.[p.value] ?? 0}
              onSelect={() => setStatus(p.value)}
            >
              {p.label}
            </FilterPill>
          ))}
        </FilterPills>
        {canManage && repo.mode === 'local' && (
          <Checkbox
            label="Custom items to assign"
            checked={toAssign}
            onChange={(e) => {
              setToAssign(e.target.checked)
              setOffset(0)
            }}
          />
        )}
        <SearchInput
          listKeys
          className="max-sm:w-full sm:ml-auto sm:w-64"
          value={search}
          onChange={(v) => {
            setSearch(v)
            setOffset(0)
          }}
          placeholder={restaurant ? 'Number, customer or table…' : 'Number or customer…'}
        />
      </ListToolbar>
      <ListFrame ref={listRef}>
        {!page.data ? (
          <SkeletonTable cols={5} />
        ) : page.data.rows.length === 0 ? (
          <EmptyState
            icon={<FileText className="size-6" />}
            title={
              unassignedCustom
                ? 'Nothing to assign'
                : search || status
                  ? 'No invoices match'
                  : 'No invoices yet'
            }
            description={
              unassignedCustom
                ? 'Every custom item has its products assigned.'
                : search || status
                  ? 'Try another status or search.'
                  : 'Invoices from Quick bill show up here.'
            }
          />
        ) : (
          <>
            <Table stack="cards">
              <thead>
                <tr>
                  <TH>Invoice</TH>
                  <TH>Date</TH>
                  <TH>Where it stands</TH>
                  <TH>Status</TH>
                  <TH align="right">Total</TH>
                  <TH />
                </tr>
              </thead>
              <tbody>
                {page.data.rows.map((inv) => {
                  const balance = balanceOf(open, inv.id)
                  const name = inv.number ?? 'Not numbered yet'
                  const owes = inv.status === 'finalized' && (balance ?? 0) > 0
                  const served = orderInfo?.get(inv.id)
                  const ServedIcon = served ? TYPE_LOOK[served.serviceType].icon : null
                  return (
                    <TR
                      key={inv.id}
                      onClick={() => openItem(`/invoices/${inv.id}`)}
                      aria-current={active === inv.id ? 'true' : undefined}
                      className={cx(
                        inv.status === 'cancelled' && CANCELLED_ROW,
                        arrived.has(inv.id) && arrivalClass,
                        active === inv.id && ROW_ACTIVE,
                      )}
                    >
                      <TD className="stack-title">
                        <RowTitle
                          tile={
                            served && ServedIcon ? (
                              <IconTile area={TYPE_LOOK[served.serviceType].area} size="sm">
                                <ServedIcon className="size-4" aria-hidden />
                              </IconTile>
                            ) : (
                              <IconTile area="sell" size="sm">
                                <ReceiptIcon className="size-4" />
                              </IconTile>
                            )
                          }
                          title={
                            <Link
                              to={`/invoices/${inv.id}`}
                              className="hover:underline focus-visible:underline"
                              onClick={(e) => {
                                e.stopPropagation()
                                moveClick(`/invoices/${inv.id}`, 'open')(e)
                              }}
                            >
                              {name}
                            </Link>
                          }
                          sub={
                            served
                              ? invoiceOrderLine(
                                  served,
                                  inv.customer_name,
                                  serverName(served.serverId),
                                )
                              : (inv.customer_name ?? 'Walk-in')
                          }
                        />
                      </TD>
                      <TD
                        className="whitespace-nowrap text-muted max-sm:hidden"
                        title={dateLabel(inv.invoice_date, settings)}
                      >
                        {dateShort(inv.invoice_date, settings)}
                      </TD>
                      <TD className="stack-r3">
                        <RowNote
                          {...invoiceStanding(
                            inv,
                            balance,
                            today,
                            (d) => dateShort(d, settings),
                            (p) => money(p, settings),
                          )}
                        />
                      </TD>
                      <TD className="stack-end stack-r2">
                        <StateSwap state={`${inv.status}:${balance}`}>
                          <StatusBadge invoice={inv} balancePaisa={balance} />
                        </StateSwap>
                      </TD>
                      <TD align="right" className="stack-end stack-r1 whitespace-nowrap font-bold">
                        {money(inv.total_paisa, settings)}
                      </TD>
                      <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                        <RowActions>
                          {inv.status === 'finalized' && inv.number && (
                            <BillPrintMenu
                              invoiceId={inv.id}
                              serverName={serverName}
                              label={`Print ${name}`}
                              triggerClassName={ROW_ICON}
                            />
                          )}
                          {owes && canPay && (
                            <Button
                              size="sm"
                              variant="soft"
                              className={ROW_BUTTON}
                              aria-label={`Take payment for ${name}`}
                              onClick={() => setPaying({ invoice: inv, balance: balance! })}
                            >
                              Take payment
                            </Button>
                          )}
                        </RowActions>
                      </TD>
                    </TR>
                  )
                })}
              </tbody>
            </Table>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>Newest first. A row opens the bill.</span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open <Kbd>P</Kbd> print{' '}
                {canPay && (
                  <>
                    <Kbd>T</Kbd> take payment
                  </>
                )}
              </span>
            </div>
            <Pagination offset={offset} limit={PAGE} total={page.data.total} onChange={setOffset} />
          </>
        )}
      </ListFrame>
      {paying && (
        <PaymentDialog
          balancePaisa={paying.balance}
          busy={busy}
          onClose={() => setPaying(null)}
          onSubmit={(amount, method, reference) => void pay(amount, method, reference)}
        />
      )}
    </div>
  )
}

export function InvoiceDetailScreen() {
  const { id = '' } = useParams()
  const repo = useRepo()
  const serverName = useServerName()
  const settings = useSettings()
  const { current } = useWorkspace()
  const { platform } = useSession()
  const online = useOnline()
  const canManage = useCan('manager')
  const canStaff = useCan('waiter')
  const toast = useToast()
  const [dialog, setDialog] = useState<null | 'pay' | 'cancel' | 'credit'>(null)
  const [assignLine, setAssignLine] = useState<InvoiceLineRow | null>(null)
  const [unassign, setUnassign] = useState<LineProductRow | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [printing, setPrinting] = useState(false)
  const detail = useLive(
    (r) => r.invoices.detail(id),
    [id],
    [
      'sales_invoices',
      'sales_invoice_lines',
      'payments',
      'payment_allocations',
      'credit_notes',
      'stock_movements',
      'invoice_line_products',
    ],
  )
  const assignedIds = [...new Set(detail.data?.assignments.map((a) => a.product_id) ?? [])]
  const assignedProducts =
    useLive(
      async (r) => {
        const rows = await Promise.all(assignedIds.map((pid) => r.get('products', pid)))
        return new Map(rows.filter((p) => p !== null).map((p) => [p.id, p] as const))
      },
      [assignedIds.join(',')],
      ['products'],
    ).data ?? new Map<string, ProductRow>()
  const payments = useLive(
    async (r) => {
      const allocations = (
        await r.list('payment_allocations', { where: { invoice_id: id }, limit: 100 })
      ).rows
      const rows = await Promise.all(
        allocations.map(async (a) => ({ a, p: await r.get('payments', a.payment_id) })),
      )
      return rows.filter((x) => x.p)
    },
    [id],
    ['payments', 'payment_allocations'],
  ).data
  const credits =
    useLive(
      (r) => r.list('credit_notes', { where: { invoice_id: id }, limit: 50 }),
      [id],
      ['credit_notes'],
    ).data?.rows ?? []
  const batchLabels = useBatchLabels(detail.data?.lines.map((l) => l.batch_id) ?? [])
  // Every print of the bill, from any device (docs/plans/print-copies.md).
  const printCount =
    useLive(
      (r) => r.list('invoice_prints', { where: { invoice_id: id }, limit: 1000 }),
      [id],
      ['invoice_prints'],
    ).data?.rows.length ?? 0

  if (!detail.data)
    return detail.loading ? <SkeletonDetail /> : <EmptyState title="Invoice not found" />
  const { invoice, lines, movements, assignments, balancePaisa } = detail.data
  const ctx = () => ({ tenantId: current!.tenant_id, now: new Date().toISOString() })

  const run = async (fn: () => Promise<void>, success: string) => {
    setBusy(true)
    try {
      await fn()
      toast.success(success)
      setDialog(null)
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const share = async () => {
    setBusy(true)
    try {
      const { url } = await createApi(platform.apiUrl, platform.supabase).shareInvoice(
        invoice.tenant_id,
        invoice.id,
      )
      setShareUrl(url)
      if (await copyText(url)) toast.success('Link copied')
      else toast.success('Link ready. Tap copy or share below.')
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <Link
        to="/invoices"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Invoices
      </Link>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {invoice.number ??
              (invoice.status === 'draft' ? 'Draft invoice' : 'Invoice (number on sync)')}{' '}
            <StatusBadge invoice={invoice} balancePaisa={balancePaisa} />
          </span>
        }
        subtitle={`${dateLabel(invoice.invoice_date, settings)} · ${invoice.customer_name ?? 'Walk-in customer'}`}
        more={
          invoice.status === 'finalized' &&
          canManage && (
            <Button
              variant="ghost"
              className="text-danger hover:text-danger"
              icon={<Ban className="size-4" />}
              onClick={() => setDialog('cancel')}
            >
              Cancel invoice
            </Button>
          )
        }
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Printer className="size-4" />}
              onClick={() => setPrinting(true)}
            >
              Print or save
            </Button>
            {invoice.status !== 'draft' && (
              <Button
                variant="secondary"
                icon={<Link2 className="size-4" />}
                disabled={!online || !invoice.number}
                loading={busy && !dialog}
                onClick={() => void share()}
                title={!invoice.number ? 'Sync first' : undefined}
              >
                Share link
              </Button>
            )}
          </>
        }
      />
      {printing && (
        <PrintDialog
          invoiceId={invoice.id}
          serverName={serverName}
          onClose={() => setPrinting(false)}
        />
      )}
      {shareUrl && (
        <ShareLinkCard
          url={shareUrl}
          title={`Invoice ${invoice.number ?? ''}`.trim()}
          text={`Invoice ${invoice.number ?? ''} from ${settings?.business_name || 'us'}`}
        />
      )}

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card className="p-0">
          <Table stack>
            <thead>
              <tr>
                <TH>#</TH>
                <TH>Item</TH>
                <TH align="right">Qty</TH>
                <TH align="right">Rate</TH>
                <TH align="right">Discount</TH>
                <TH align="right">Amount</TH>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <TD className="text-muted max-sm:hidden">{l.line_no}</TD>
                  <TD className="stack-main stack-r1">
                    {/* What was billed, linked to the product (on screen only; bills print plain). */}
                    {l.product_id ? (
                      <Link
                        to={`/products/${l.product_id}`}
                        className="font-medium text-ink hover:text-primary hover:underline"
                      >
                        {l.description}
                      </Link>
                    ) : (
                      <>
                        {l.description} <Badge className="ml-1 align-middle">Custom</Badge>
                      </>
                    )}
                    <div className="text-xs text-muted">
                      {[
                        lineVatLabel(registrationOf(settings), l.tax_rate_bp),
                        batchText(l, batchLabels),
                        serialText(l),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                    {/* Which products a custom item was (never printed; the bill stays as it is). */}
                    {!l.product_id && (
                      <div className="mt-1 space-y-1">
                        {assignments
                          .filter((a) => a.invoice_line_id === l.id)
                          .map((a) => (
                            <div key={a.id} className="flex items-center gap-1 text-xs">
                              <span>
                                {formatQty(a.qty_milli)} ×{' '}
                                <Link
                                  to={`/products/${a.product_id}`}
                                  className="font-medium hover:text-primary hover:underline"
                                >
                                  {assignedProducts.get(a.product_id)?.name ?? 'Product'}
                                </Link>
                                <span className="hidden text-muted sm:inline">
                                  {' '}
                                  · assigned {dateShort(a.created_at.slice(0, 10), settings)}
                                </span>
                              </span>
                              {canManage && invoice.status === 'finalized' && (
                                <IconButton
                                  label={`Remove ${assignedProducts.get(a.product_id)?.name ?? 'product'} from ${l.description}`}
                                  className="size-6"
                                  onClick={() => setUnassign(a)}
                                >
                                  <X className="size-3.5" />
                                </IconButton>
                              )}
                            </div>
                          ))}
                        {canManage && invoice.status === 'finalized' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            icon={<PackagePlus className="size-4" />}
                            aria-label={`Assign products to ${l.description}`}
                            onClick={() => setAssignLine(l)}
                          >
                            Assign products
                          </Button>
                        )}
                      </div>
                    )}
                  </TD>
                  <TD
                    align="right"
                    className="stack-start stack-r2 max-sm:text-left max-sm:text-muted max-sm:before:content-['Qty_']"
                  >
                    {lineQtyText(l)}
                  </TD>
                  <TD
                    align="right"
                    className="stack-mid stack-r2 max-sm:text-muted max-sm:before:mr-0.5 max-sm:before:content-['@']"
                  >
                    {money(l.unit_price_paisa, settings)}
                  </TD>
                  <TD
                    align="right"
                    className={cx(
                      'stack-end stack-r2 max-sm:text-muted',
                      !l.discount_paisa && 'max-sm:hidden',
                    )}
                  >
                    {l.discount_paisa ? (
                      <>
                        <span className="sm:hidden">less </span>
                        {money(l.discount_paisa, settings)}
                      </>
                    ) : (
                      ''
                    )}
                  </TD>
                  <TD align="right" className="stack-end stack-r1 max-sm:font-medium">
                    {money(l.total_paisa, settings)}
                  </TD>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>

        <div className="space-y-4">
          <Card>
            <dl className="space-y-1.5 text-sm">
              <Line label="Subtotal" value={money(invoice.subtotal_paisa, settings)} />
              {invoice.discount_paisa > 0 && (
                <Line label="Discount" value={`−${money(invoice.discount_paisa, settings)}`} />
              )}
              {showsVat(registrationOf(settings), invoice.tax_paisa) && (
                <>
                  <Line label="Taxable" value={money(invoice.taxable_paisa, settings)} />
                  <Line label="VAT" value={money(invoice.tax_paisa, settings)} />
                </>
              )}
              {invoice.round_off_paisa !== 0 && (
                <Line label="Round off" value={money(invoice.round_off_paisa, settings)} />
              )}
              <div className="flex justify-between border-t border-border pt-2 text-base font-semibold">
                <dt>Total</dt>
                <dd className="tabular">{money(invoice.total_paisa, settings)}</dd>
              </div>
              {balancePaisa !== null && (
                <div
                  className={`flex justify-between font-medium ${balancePaisa > 0 ? 'text-warning' : balancePaisa < 0 ? 'text-danger' : 'text-success'}`}
                >
                  <dt>
                    {balancePaisa > 0
                      ? 'Balance due'
                      : balancePaisa < 0
                        ? 'Refund due to customer'
                        : 'Paid in full'}
                  </dt>
                  <dd className="tabular">
                    {balancePaisa !== 0 ? money(Math.abs(balancePaisa), settings) : ''}
                  </dd>
                </div>
              )}
            </dl>
            {printCount > 0 && (
              <p className="mt-2 text-xs text-muted">
                Printed {printCount === 1 ? 'once' : `${printCount} times`}
              </p>
            )}
            {invoice.notes && (
              <p className="mt-3 rounded-xl bg-soft p-2 text-sm">
                <span className="text-muted">Note: </span>
                {invoice.notes}
              </p>
            )}
            {invoice.status === 'cancelled' && (
              <p className="mt-3 rounded-xl bg-danger-light p-2 text-sm text-danger">
                Cancelled: {invoice.cancel_reason}
              </p>
            )}
            {invoice.status === 'finalized' && canStaff && (
              <div className="mt-4 flex flex-wrap gap-2">
                {balancePaisa !== null && balancePaisa > 0 && (
                  <Button
                    size="sm"
                    icon={<Wallet className="size-4" />}
                    onClick={() => setDialog('pay')}
                  >
                    Record payment
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<RotateCcw className="size-4" />}
                  onClick={() => setDialog('credit')}
                >
                  Return / credit
                </Button>
              </div>
            )}
          </Card>

          {(payments?.length ?? 0) > 0 && (
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Payments</h3>
              <ul className="space-y-1 text-sm">
                {payments!.map(({ a, p }) => (
                  <li key={a.id} className="flex justify-between gap-3">
                    <span
                      className="min-w-0 capitalize text-muted"
                      title={dateLabel(p!.payment_date, settings)}
                    >
                      {p!.method} · {dateShort(p!.payment_date, settings)}
                      {p!.status === 'void' && ' (void)'}
                    </span>
                    <span className="tabular shrink-0 whitespace-nowrap">
                      {money(a.amount_paisa, settings)}
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          {credits.length > 0 && (
            <Card>
              <h3 className="mb-2 text-sm font-semibold">Credit notes</h3>
              <ul className="space-y-1 text-sm">
                {credits.map((c) => (
                  <li key={c.id} className="flex justify-between">
                    <span className="text-muted">
                      {c.number ?? 'Number on sync'} · {c.reason}
                    </span>
                    <span className="tabular">−{money(c.total_paisa, settings)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>

      {dialog === 'pay' && balancePaisa !== null && (
        <PaymentDialog
          balancePaisa={balancePaisa}
          busy={busy}
          onClose={() => setDialog(null)}
          onSubmit={(amount, method, reference) =>
            run(async () => {
              await repo.commit(
                buildPayment(ctx(), {
                  customerId: invoice.customer_id,
                  amountPaisa: amount,
                  method,
                  paymentDate: todayIso(),
                  reference: reference || null,
                  allocations: [{ invoiceId: invoice.id, amountPaisa: amount }],
                }).ops,
              )
            }, 'Payment recorded')
          }
        />
      )}

      <ConfirmDialog
        open={dialog === 'cancel'}
        title="Cancel this invoice?"
        message="The number stays used and the stock goes back. This can’t be undone; use a credit note for partial returns."
        confirmLabel="Cancel invoice"
        danger
        busy={busy}
        onClose={() => setDialog(null)}
        onConfirm={() =>
          cancelReason.trim()
            ? void run(async () => {
                // Units sold on this invoice go back into stock with it.
                const units = []
                for (const l of lines) {
                  if (lineSerials(l.serials).length === 0) continue
                  units.push(
                    ...(
                      await repo.list('product_serials', {
                        where: { sold_line_id: l.id },
                        limit: 1000,
                      })
                    ).rows,
                  )
                }
                await repo.commit([
                  ...cancelInvoice(ctx(), stripServer(invoice), movements, cancelReason).ops,
                  ...cancelSerialOps(ctx(), units),
                ])
              }, 'Invoice cancelled')
            : toast.error('Enter a reason')
        }
      >
        <Field label="Reason" className="mt-3">
          {(fid) => (
            <Textarea
              id={fid}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. wrong customer"
            />
          )}
        </Field>
      </ConfirmDialog>

      {dialog === 'credit' && (
        <CreditNoteDialog
          busy={busy}
          lines={lines}
          assignedLineIds={new Set(assignments.map((a) => a.invoice_line_id))}
          batches={batchLabels}
          onClose={() => setDialog(null)}
          onSubmit={(picked, reason, restock, locationId) =>
            run(async () => {
              const today = todayIso()
              const numbering = await numberingFor(repo, settings!, 'credit_note', today)
              // Only products that track stock are restocked (read at the moment of the write).
              const tracked = new Map<string, boolean>()
              for (const p of picked) {
                const productId = p.line.product_id
                if (productId && !tracked.has(productId))
                  tracked.set(
                    productId,
                    (await repo.get('products', productId))?.track_stock ?? true,
                  )
              }
              // Restocking a dish puts its ingredients back, by its recipe as it is now.
              const recipes = restock
                ? await loadRecipes(
                    repo,
                    picked.map((p) => p.line.product_id),
                  )
                : new Map<string, never>()
              const built = buildCreditNote(ctx(), {
                status: 'finalized',
                invoice: stripServer(invoice),
                lines: picked.map((p) => ({
                  invoiceLine: stripServerLine(p.line),
                  qtyMilli: p.qtyMilli,
                  trackStock: p.line.product_id ? tracked.get(p.line.product_id) : false,
                  components: p.line.product_id ? (recipes.get(p.line.product_id) ?? null) : null,
                  ...(p.serials ? { serials: p.serials } : {}),
                })),
                noteDate: today,
                reason,
                restock,
                locationId,
                numbering,
              })
              const units = new Map<string, SerialRow | null>()
              for (const p of picked) {
                if (!p.serials || !p.line.product_id) continue
                for (const [k, v] of await loadSerials(
                  repo,
                  current!.tenant_id,
                  p.line.product_id,
                  p.serials,
                ))
                  units.set(k, v)
              }
              // A custom item returned whole brings its assigned products back into stock.
              const assignedBack: SyncOp[] = []
              if (restock && locationId) {
                for (const p of picked) {
                  if (p.line.product_id || p.qtyMilli !== p.line.qty_milli) continue
                  for (const a of assignments.filter((x) => x.invoice_line_id === p.line.id)) {
                    const product = await repo.get('products', a.product_id)
                    if (product && !product.track_stock) continue
                    assignedBack.push({
                      table: 'stock_movements',
                      op: 'upsert',
                      row: {
                        ...stockReturn(ctx(), a, 'credit_note', built.creditNote.id, locationId),
                      },
                    })
                  }
                }
              }
              await repo.commit([
                ...built.ops,
                ...assignedBack,
                ...returnSerialOps(ctx(), built.lines, units, restock),
              ])
            }, 'Credit note created')
          }
        />
      )}

      {assignLine && (
        <AssignProductsDialog
          invoice={invoice}
          line={assignLine}
          onClose={() => setAssignLine(null)}
        />
      )}
      <ConfirmDialog
        open={unassign !== null}
        title="Remove this product from the item?"
        message="Its stock goes back. The bill does not change."
        confirmLabel="Remove"
        busy={busy}
        onClose={() => setUnassign(null)}
        onConfirm={() => {
          const row = unassign
          if (!row) return
          void run(async () => {
            const product = await repo.get('products', row.product_id)
            await repo.commit(unassignLineProduct(ctx(), row, product?.track_stock ?? true).ops)
            setUnassign(null)
          }, 'Product removed, stock returned')
        }}
      />
    </div>
  )
}

/**
 * Says which products a custom item really was. The bill keeps its text and
 * amount; the stock of the chosen products goes out now.
 */
function AssignProductsDialog({
  invoice,
  line,
  onClose,
}: {
  invoice: InvoiceRow
  line: InvoiceLineRow
  onClose: () => void
}) {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const [items, setItems] = useState<{ product: ProductRow; qtyMilli: number | null }[]>([])
  const [locationId, setLocationId] = useState<string | null>(invoice.location_id)
  const location = locationId ?? lookups.activeLocations[0]?.id ?? null
  const [busy, setBusy] = useState(false)
  const [short, setShort] = useState<StockShortfall[] | null>(null)

  const pick = (p: ProductRow) => {
    if (p.track_serials) {
      toast.error(`${p.name} has serial numbers, so it can’t be assigned. Sell it as its own line.`)
      return
    }
    setItems((list) =>
      list.some((x) => x.product.id === p.id)
        ? list.map((x) =>
            x.product.id === p.id ? { ...x, qtyMilli: (x.qtyMilli ?? 0) + 1000 } : x,
          )
        : [...list, { product: p, qtyMilli: 1000 }],
    )
  }
  const invalid = items.length === 0 || items.some((x) => !x.qtyMilli || x.qtyMilli <= 0)

  /** `anyway`: the manager confirmed taking stock below zero. */
  const save = async (anyway = false) => {
    if (!current || !settings || invalid) return
    if (!location) return toast.error('Choose the location the stock comes from')
    setBusy(true)
    try {
      const saleLines = items.map((x) => ({
        productId: x.product.id,
        description: x.product.name,
        qtyMilli: x.qtyMilli!,
        unitPricePaisa: 0,
        priceIncludesTax: true,
        taxRateBp: 0,
        discountPaisa: 0,
        trackStock: x.product.track_stock,
        trackBatches: x.product.track_batches,
      }))
      if (!anyway) {
        const levels = await repo.stock.levels({ locationId: location })
        const lacking = stockShortfalls(
          saleLines,
          new Map(levels.map((l) => [l.product_id, l.qty_milli] as const)),
        )
        if (lacking.length > 0) {
          setShort(lacking)
          return
        }
      }
      // Batch products leave their batches earliest expiry first, as on a bill.
      const plan = planBatchLines(
        saleLines,
        await loadBatchStock(
          repo,
          items.filter((x) => x.product.track_batches).map((x) => x.product.id),
          location,
        ),
        todayIso(),
        settings.expired_sale,
      )
      if (plan.blocked) return toast.error(plan.blocked)
      const built = assignLineProducts(
        { tenantId: current.tenant_id, now: new Date().toISOString() },
        {
          invoice: stripServer(invoice),
          line: stripServerLine(line),
          locationId: location,
          items: plan.lines.map((l) => ({
            productId: l.productId!,
            qtyMilli: l.qtyMilli,
            batchId: l.batchId ?? null,
            trackStock: l.trackStock,
          })),
        },
      )
      await repo.commit(built.ops)
      toast.success('Products assigned')
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Assign products"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} disabled={invalid} onClick={() => void save()}>
            Assign and take from stock
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="rounded-xl bg-soft p-3 text-sm">
          <span className="font-medium">{line.description}</span>
          <span className="text-muted">
            {' '}
            · the bill stays {money(line.total_paisa, settings)}. Only stock changes.
          </span>
        </p>
        <ProductPicker
          onPick={(p) => pick(p)}
          placeholder="Search the products it was…"
          autoFocus={finePointer}
        />
        {items.length > 0 && (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {items.map((x) => (
              <li key={x.product.id} className="flex items-center gap-3 p-2 pl-3">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {x.product.name}
                </span>
                <div className="w-24 shrink-0">
                  <QtyInput
                    aria-label={`Quantity of ${x.product.name}`}
                    milli={x.qtyMilli}
                    onValue={(m) =>
                      setItems((list) =>
                        list.map((y) =>
                          y.product.id === x.product.id ? { ...y, qtyMilli: m } : y,
                        ),
                      )
                    }
                  />
                </div>
                <IconButton
                  label={`Remove ${x.product.name}`}
                  onClick={() =>
                    setItems((list) => list.filter((y) => y.product.id !== x.product.id))
                  }
                >
                  <X className="size-4" />
                </IconButton>
              </li>
            ))}
          </ul>
        )}
        {lookups.activeLocations.length > 1 && (
          <Field label="Take stock from">
            {(id) => (
              <LocationSelect
                id={id}
                locations={lookups.activeLocations}
                value={location}
                onChange={setLocationId}
              />
            )}
          </Field>
        )}
      </div>
      <ConfirmDialog
        open={short !== null}
        title="Take more than you have in stock?"
        message={
          <span>
            {short?.map((x) => (
              <span key={x.productId} className="block">
                {x.description}: assigning {formatQty(x.needMilli)}, only{' '}
                {formatQty(Math.max(0, x.haveMilli))} in stock.
              </span>
            ))}
            <span className="mt-2 block text-muted">Stock will go below zero.</span>
          </span>
        }
        confirmLabel="Assign anyway"
        onClose={() => setShort(null)}
        onConfirm={() => {
          setShort(null)
          void save(true)
        }}
      />
    </Modal>
  )
}

/** Copies text; false when the browser refuses (no permission, insecure page). */
async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** The public invoice link as a QR code, with copy and (on phones) the system share sheet. */
function ShareLinkCard({ url, title, text }: { url: string; title: string; text: string }) {
  const toast = useToast()
  const [copied, setCopied] = useState(false)
  const canShare = typeof navigator.share === 'function'
  const copy = async () => {
    if (await copyText(url)) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } else toast.error('Couldn’t copy. Press and hold the link to copy it.')
  }
  const shareNative = async () => {
    try {
      await navigator.share({ title, text, url })
    } catch (error) {
      // Closing the share sheet is not an error.
      if (!(error instanceof DOMException && error.name === 'AbortError'))
        toast.error(errorMessage(error))
    }
  }
  return (
    <Card className="mb-4 flex items-center gap-4 text-sm">
      {/* The link itself is not shown: the code, Copy link and Share carry it. White in the dark
          theme too, as a phone cannot read a QR drawn light on dark. */}
      <span className="block w-28 shrink-0 overflow-hidden rounded-xl border border-[#e5e7eb] bg-[#fff]">
        <QrCode value={url} label="QR code for the invoice link" />
      </span>
      <div className="flex min-w-0 flex-col gap-2">
        <div>
          <p className="font-semibold">Public link ready</p>
          <p className="text-xs text-muted">Scan it on a phone, or copy or share the link.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            onClick={() => void copy()}
            aria-live="polite"
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          {canShare && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Share2 className="size-4" />}
              onClick={() => void shareNative()}
            >
              Share
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}

// Rows read locally carry server-set columns; builders take writable rows.
function stripServer(invoice: InvoiceRow) {
  const { server_updated_at: _s, created_by: _c, ird_status: _i, ...rest } = invoice
  return rest
}
function stripServerLine<T extends { server_updated_at?: unknown }>(line: T) {
  const { server_updated_at: _s, ...rest } = line
  return rest as Omit<T, 'server_updated_at'>
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="tabular">{value}</dd>
    </div>
  )
}

export function PaymentDialog({
  balancePaisa,
  busy,
  onClose,
  onSubmit,
  title = 'Record payment',
  detail,
  ahead = false,
}: {
  balancePaisa: number
  busy: boolean
  onClose: () => void
  onSubmit: (amount: number, method: PaymentMethod, reference: string) => void
  title?: string
  /** A line under the form (where the money goes). */
  detail?: ReactNode
  /** A customer's payment may be more than they owe: the rest is kept as paid ahead. */
  ahead?: boolean
}) {
  const [amount, setAmount] = useState<number | null>(balancePaisa > 0 ? balancePaisa : null)
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [reference, setReference] = useState('')
  const over = amount !== null && amount > balancePaisa ? amount - balancePaisa : 0
  const invalid = !amount || amount <= 0 || (over > 0 && !ahead)
  return (
    <Modal
      open
      onClose={onClose}
      area="sell"
      icon={<Banknote className="size-5" />}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={invalid}
            onClick={() => onSubmit(amount!, method, reference)}
          >
            Save payment
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label="Amount"
          hint={balancePaisa > 0 ? `Balance due ${money(balancePaisa)}` : 'Nothing is owed now'}
          error={over > 0 && !ahead ? 'More than the balance due' : null}
        >
          {(id) => <MoneyInput id={id} paisa={amount} onValue={setAmount} />}
        </Field>
        <Field label="Method">
          {(id) => (
            <Select
              id={id}
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
            >
              <option value="cash">Cash</option>
              <option value="bank">Bank transfer</option>
              <option value="card">Card</option>
              <option value="wallet">Wallet</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </Select>
          )}
        </Field>
        <Field label="Reference (optional)">
          {(id) => (
            <Input id={id} value={reference} onChange={(e) => setReference(e.target.value)} />
          )}
        </Field>
        {detail && <p className="text-sm text-muted">{detail}</p>}
        {ahead && over > 0 && (
          <p className="rounded-lg bg-warning-light px-3 py-2 text-sm text-warning">
            {money(over)} more than they owe is kept as paid ahead. It comes off their next bill.
          </p>
        )}
      </div>
    </Modal>
  )
}

function CreditNoteDialog({
  lines,
  assignedLineIds,
  batches,
  busy,
  onClose,
  onSubmit,
}: {
  lines: InvoiceLineRow[]
  /** Custom items that have products assigned (their stock returns with a whole-line return). */
  assignedLineIds: ReadonlySet<string>
  batches: BatchLabels
  busy: boolean
  onClose: () => void
  onSubmit: (
    picked: { line: InvoiceLineRow; qtyMilli: number; serials?: string[] }[],
    reason: string,
    restock: boolean,
    locationId: string | null,
  ) => void
}) {
  const lookups = useLookups()
  const [qtys, setQtys] = useState<Record<string, number | null>>({})
  // Serial lines: the units being returned, by line.
  const [returned, setReturned] = useState<Record<string, string[]>>({})
  const serialLineIds = lines.filter((l) => lineSerials(l.serials).length > 0).map((l) => l.id)
  // Units still sold on these lines (a unit returned before can't be returned again).
  const stillSold =
    useLive(
      async (r) => {
        const out = new Set<string>()
        for (const id of serialLineIds) {
          const rows = (
            await r.list('product_serials', { where: { sold_line_id: id }, limit: 1000 })
          ).rows
          for (const u of rows) if (u.status === 'sold') out.add(`${id}:${u.serial}`)
        }
        return out
      },
      [serialLineIds.join(',')],
      ['product_serials'],
    ).data ?? new Set<string>()
  const [reason, setReason] = useState('')
  const [restock, setRestock] = useState(true)
  const [locationId, setLocationId] = useState<string | null>(null)
  const location = locationId ?? lookups.activeLocations[0]?.id ?? null
  const picked = lines.flatMap((l) => {
    const serials = returned[l.id]
    if (serials?.length) return [{ line: l, qtyMilli: serials.length * 1000, serials }]
    // Typed in the unit it was sold in; the note holds the stock unit's share.
    const typed = qtys[l.id] ?? 0
    const q = l.sold_qty_milli ? mulDivRound(typed, l.qty_milli, l.sold_qty_milli) : typed
    return q > 0 && lineSerials(l.serials).length === 0 ? [{ line: l, qtyMilli: q }] : []
  })
  const invalid =
    picked.length === 0 || !reason.trim() || picked.some((p) => p.qtyMilli > p.line.qty_milli)
  return (
    <Modal
      open
      onClose={onClose}
      area="sell"
      icon={<ReceiptIcon className="size-5" />}
      title="Return / credit note"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={invalid}
            onClick={() => onSubmit(picked, reason.trim(), restock, restock ? location : null)}
          >
            Create credit note
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Table>
          <thead>
            <tr>
              <TH>Item</TH>
              <TH align="right">Sold</TH>
              <TH align="right">Return</TH>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <TD>
                  {l.description}
                  {batchText(l, batches) && (
                    <div className="text-xs text-muted">{batchText(l, batches)}</div>
                  )}
                  {assignedLineIds.has(l.id) && (
                    <div className="text-xs text-muted">
                      Its assigned products go back into stock only when the whole item is returned.
                    </div>
                  )}
                </TD>
                <TD align="right">{lineQtyText(l)}</TD>
                <TD align="right" className="w-32">
                  {lineSerials(l.serials).length > 0 ? (
                    <div className="flex flex-col items-end gap-1">
                      {lineSerials(l.serials).map((serial) => (
                        <Checkbox
                          key={serial}
                          label={<span className="font-mono text-xs">{serial}</span>}
                          aria-label={`Return ${serial}`}
                          disabled={!stillSold.has(`${l.id}:${serial}`)}
                          checked={returned[l.id]?.includes(serial) ?? false}
                          onChange={(e) =>
                            setReturned((r) => ({
                              ...r,
                              [l.id]: e.target.checked
                                ? [...(r[l.id] ?? []), serial]
                                : (r[l.id] ?? []).filter((x) => x !== serial),
                            }))
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <QtyInput
                      aria-label={`Return quantity for ${l.description}${batchText(l, batches) ? ` (${batchText(l, batches)})` : ''}`}
                      milli={qtys[l.id] ?? null}
                      placeholder="0"
                      onValue={(m) => setQtys((q) => ({ ...q, [l.id]: m }))}
                    />
                  )}
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
        <Field label="Reason">
          {(id) => (
            <Input
              id={id}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. damaged item returned"
            />
          )}
        </Field>
        <Checkbox
          label="Put returned items back into stock"
          checked={restock}
          onChange={(e) => setRestock(e.target.checked)}
        />
        {restock && lookups.activeLocations.length > 1 && (
          <Field label="Return to">
            {(id) => (
              <LocationSelect
                id={id}
                locations={lookups.activeLocations}
                value={location}
                onChange={setLocationId}
              />
            )}
          </Field>
        )}
      </div>
    </Modal>
  )
}
